import { useId, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { Box, Button, Stack, Typography, TextField, keyframes } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useSyncTranslationConfig, useTranslation } from "@privacy/translations";
import { z } from "zod";
import { requestCodeMutationOptions, verifyCodeMutationOptions } from "./trustAuthAPI";
import { TrustPageHeader } from "./TrustHeader";
import { TRUST_PATHS } from "./paths";
import { getStoredTrustLanguage } from "./trustLanguage";
import * as FormDialog from "../shared/FormDialog";
import { flushSync } from "react-dom";

type Step = "email" | "otp";

export const TrustLoginPage = () => {
  const { t } = useTranslation();
  const requestCodeMutation = useMutation(requestCodeMutationOptions());
  const [email, setEmail] = useState("");

  const [step, setStep] = useState<Step>("email");

  useSyncTranslationConfig({ language: getStoredTrustLanguage() ?? "en" });

  return (
    <>
      <TrustPageHeader.NotAuthenticated />
      <Stack
        sx={{ height: `calc(100dvh - ${TrustPageHeader.HEIGHT}px)`, justifyContent: "center", alignItems: "center" }}
      >
        <Stack sx={{ px: 2, transform: "translateY(-30%)", width: "min(550px, 100%)" }}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="h1" sx={{ fontSize: theme => theme.typography.pxToRem(28), fontWeight: 600, mb: 1 }}>
              {t("Trust.Login.Title")}
            </Typography>
            {(() => {
              switch (step) {
                case "email":
                  return (
                    <>
                      <SubtitleWrapper>
                        <Subtitle>{t("Trust.Login.Subtitle")}</Subtitle>
                      </SubtitleWrapper>

                      <EmailStep
                        onSubmited={email => {
                          setEmail(email);
                          setStep("otp");
                        }}
                      />
                    </>
                  );
                case "otp":
                  return (
                    <>
                      <SubtitleWrapper>
                        <Subtitle>{t("Trust.Otp.EnterCode")}</Subtitle>
                        <Button
                          variant="text"
                          sx={{
                            "p": 0,
                            "position": "relative",
                            "lineHeight": 1,
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              inset: 0,
                              height: "20px",
                              transform: "scale(2)",
                            },
                          }}
                          loading={requestCodeMutation.isPending}
                          onClick={() => requestCodeMutation.mutate({ email })}
                        >
                          {t("Trust.Otp.ResendCode")}
                        </Button>
                      </SubtitleWrapper>

                      <OtpStep email={email} onBack={() => setStep("email")} />
                    </>
                  );
                default:
                  throw new Error(`Unexpected trust login step: ${step}`);
              }
            })()}
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

const SubtitleWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 4 }}>{children}</Box>;
};

const Subtitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.text.secondary }}>
      {children}
    </Typography>
  );
};

const EmailFormSchema = z.object({ email: z.email() });

type EmailFormValues = z.infer<typeof EmailFormSchema>;

type EmailStepProps = { onSubmited: (email: string) => void };

const EmailStep = ({ onSubmited }: EmailStepProps) => {
  const requestCodeMutation = useMutation(requestCodeMutationOptions());
  const { t } = useTranslation();

  const { control, handleSubmit, setFocus } = useForm<EmailFormValues>({ defaultValues: { email: "" } });
  const id = useId();

  FormDialog.useAutoFocusField(setFocus, "email");

  return (
    <form
      noValidate
      onSubmit={handleSubmit(values =>
        requestCodeMutation.mutate(values, { onSuccess: () => onSubmited(values.email) })
      )}
    >
      <Stack spacing={2.5}>
        <Controller
          control={control}
          name="email"
          rules={{
            validate: value => {
              if (!value.trim()) return t("Input.Validation.Required");
              return EmailFormSchema.shape.email.safeParse(value).success || t("Input.Validation.RequiredEmail");
            },
          }}
          render={({ field: { ref, ...field }, fieldState }) => {
            return (
              <FormDialog.Field
                errorMessage={
                  fieldState.error?.message ?? (requestCodeMutation.error ? t("Trust.Login.SendCodeError") : undefined)
                }
              >
                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                  <FormDialog.Label htmlFor={id}>{t("Trust.Login.EmailLabel")}</FormDialog.Label>
                </Stack>
                <TextField
                  {...field}
                  id={id}
                  type="email"
                  size="small"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  inputRef={ref}
                />
              </FormDialog.Field>
            );
          }}
        />
        <Button type="submit" variant="contained" fullWidth loading={requestCodeMutation.isPending}>
          {t("Trust.Login.SendCode")}
        </Button>
      </Stack>
    </form>
  );
};

type OtpStepProps = {
  email: string;
  onBack: () => void;
};

const OtpStep = ({ email, onBack }: OtpStepProps) => {
  const { t } = useTranslation();
  const id = useId();
  const { returnUrl } = useReturnUrl(TRUST_PATHS.grid());
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const verifyCodeMutation = useMutation(verifyCodeMutationOptions({ returnUrl }));

  const handleComplete = (code: string) => {
    verifyCodeMutation.mutate(
      { email, code },
      {
        onError: () => {
          flushSync(() => setValue(""));
          inputRef.current?.focus();
        },
      }
    );
  };

  return (
    <Stack sx={{ gap: 2.5 }}>
      <FormDialog.Field>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <FormDialog.Label htmlFor={id}>{t("Trust.Login.EmailLabel")}</FormDialog.Label>
          <Button variant="text" size="small" onClick={onBack} sx={{ textTransform: "none" }}>
            {t("Trust.Otp.ChangeEmail")}
          </Button>
        </Stack>
        <TextField id={id} value={email} disabled size="small" fullWidth />
      </FormDialog.Field>
      <FormDialog.Field errorMessage={Boolean(verifyCodeMutation.error) && t("Trust.Otp.GenericError")}>
        <Box aria-live="polite">
          <OtpDigitBoxes
            value={value}
            onChange={value => {
              verifyCodeMutation.reset();
              setValue(value);
              if (value.length === OTP_LENGTH) handleComplete(value);
            }}
            isPending={verifyCodeMutation.isPending}
            inputRef={inputRef}
          />
        </Box>
      </FormDialog.Field>
    </Stack>
  );
};

type OtpDigitBoxesProps = {
  value: string;
  onChange: (value: string) => void;
  isPending: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
};

const OTP_LENGTH = 6;

const OtpDigitBoxes = ({ value, onChange, isPending, inputRef }: OtpDigitBoxesProps) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        component="input"
        autoFocus
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onPaste={e => {
          e.preventDefault();
          const pasted = e.clipboardData.getData("text").replace(/[\s-]/g, "").slice(0, OTP_LENGTH);
          onChange(pasted);
        }}
        onKeyDown={e => {
          if (e.key === "Backspace" && value.length === 0) e.preventDefault();
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={isPending}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        data-1p-ignore="true"
        maxLength={OTP_LENGTH}
        aria-label={t("Trust.Otp.CodeLabel")}
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          width: "100%",
          height: "100%",
          caretColor: "transparent",
          zIndex: 1,
        }}
      />
      <Box onClick={() => inputRef.current?.focus()} sx={{ display: "flex", gap: 1, width: "100%" }}>
        {Array.from({ length: OTP_LENGTH }, (_, i) => {
          const isActive = isFocused && i === value.length && !isPending;
          const isFilled = i < value.length;

          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 0,
                border: "2px solid",
                borderColor: theme => (isActive ? theme.palette.primary.main : theme.palette.divider),
                fontSize: theme => theme.typography.pxToRem(24),
                fontWeight: 600,
                transition: "border-color 0.15s ease",
                bgcolor: "transparent",
              }}
            >
              {isFilled ? value[i] : isActive ? <Caret /> : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const caretBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const Caret = () => (
  <Box
    sx={{
      width: "2px",
      height: 24,
      bgcolor: theme => theme.palette.text.primary,
      animation: `${caretBlink} 1s step-end infinite`,
    }}
  />
);

const useReturnUrl = (fallback: string) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const searchReturnUrl = searchParams.get("returnUrl");
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  return {
    returnUrl: searchReturnUrl && searchReturnUrl !== TRUST_PATHS.login() ? searchReturnUrl : fallback,
    constructSearchParams: () => new URLSearchParams({ returnUrl: currentPath }).toString(),
  };
};

TrustLoginPage.useReturnUrl = useReturnUrl;
